import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format } from 'date-fns';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';
import { HrMonthCalendarGridSkeleton } from './HrCalendarGridLoader';
import { HR_WEEKDAYS, hrIsInViewMonth, hrMonthCalendarDays, hrShiftMonth } from './hrCalendarUtils';

export interface HrMonthCalendarGridProps {
  viewDate: Date;
  onViewDateChange: (date: Date) => void;
  renderDay: (day: Date, inMonth: boolean) => ReactNode;
  onDayClick?: (day: Date) => void;
  getDayCellClassName?: (day: Date, inMonth: boolean) => string | undefined;
  getDayTitle?: (day: Date, inMonth: boolean) => string | undefined;
  loading?: boolean;
  className?: string;
}

export function HrMonthCalendarGrid({
  viewDate,
  onViewDateChange,
  renderDay,
  onDayClick,
  getDayCellClassName,
  getDayTitle,
  loading,
  className,
}: HrMonthCalendarGridProps) {
  const days = hrMonthCalendarDays(viewDate);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <Button variant="secondary" size="sm" onClick={() => onViewDateChange(hrShiftMonth(viewDate, -1))}>
          <ChevronLeftIcon fontSize="small" />
        </Button>
        <h4 className="text-sm font-semibold">{format(viewDate, 'MMMM yyyy')}</h4>
        <Button variant="secondary" size="sm" onClick={() => onViewDateChange(hrShiftMonth(viewDate, 1))}>
          <ChevronRightIcon fontSize="small" />
        </Button>
      </div>

      {loading ? (
        <HrMonthCalendarGridSkeleton />
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 rounded-md bg-slate-50 p-1 text-center text-xs font-medium text-slate-500 dark:bg-slate-800/50">
            {HR_WEEKDAYS.map((d) => (
              <div key={d} className="py-1.5">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const inMonth = hrIsInViewMonth(day, viewDate);
              const extraClass = inMonth ? getDayCellClassName?.(day, inMonth) : undefined;
              const dayTitle = inMonth ? getDayTitle?.(day, inMonth) : undefined;
              const cell = (
                <div
                  className={cn(
                    'min-h-[88px] rounded-md border p-1.5 text-xs transition-colors',
                    extraClass,
                    !extraClass &&
                      (inMonth
                        ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/80'
                        : 'border-transparent bg-slate-50/80 text-slate-400 dark:bg-slate-800/40'),
                    onDayClick && inMonth && 'cursor-pointer hover:border-primary-300 dark:hover:border-primary-700',
                  )}
                  onClick={onDayClick && inMonth ? () => onDayClick(day) : undefined}
                  onKeyDown={
                    onDayClick && inMonth
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onDayClick(day);
                          }
                        }
                      : undefined
                  }
                  role={onDayClick && inMonth ? 'button' : undefined}
                  tabIndex={onDayClick && inMonth ? 0 : undefined}
                >
                  <div className={cn('mb-1 font-medium', !inMonth && 'opacity-50')}>{format(day, 'd')}</div>
                  {inMonth ? renderDay(day, inMonth) : null}
                </div>
              );
              return (
                <Tooltip key={day.toISOString()} content={dayTitle}>
                  {cell}
                </Tooltip>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
